import { useState, useEffect, useRef, useMemo } from "react";
import { FilterOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Select } from "antd";

import SearchBar from "../SearchBar/SearchBar";
import { normalizeName, safeEquals, projectMatchesZone, zoneNameFromCode } from "../../utils/orgFilters";
import "./TopToolbar.css";

function TopToolbar({
  projects,
  filters,
  setFilters,
  onProjectSelect,
  zonesList = [],
  circlesList = [],
  districtsList = [],
  talukasList = [],
  hierarchyLoading = false,
}) {
  const trackRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateArrows = () => {
    const track = trackRef.current;
    if (!track) return;
    const hasOverflow = track.scrollWidth > track.clientWidth;
    setShowLeft(track.scrollLeft > 5);
    setShowRight(hasOverflow && track.scrollLeft < track.scrollWidth - track.clientWidth - 5);
  };

  useEffect(() => {
    setTimeout(updateArrows, 200);
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [zonesList]);

  const slideLeft = () => trackRef.current?.scrollBy({ left: -230, behavior: "smooth" });
  const slideRight = () => trackRef.current?.scrollBy({ left: 230, behavior: "smooth" });

  const selectedZoneName = filters.zone ? zoneNameFromCode(filters.zone, zonesList) : null;

  // ---- CIRCLE: everything, narrowed only by zone (if selected) ----
  const circles = useMemo(() => {
    const src = selectedZoneName
      ? circlesList.filter((c) => normalizeName(c.zone_name) === normalizeName(selectedZoneName))
      : circlesList;

    return dedupe(src.map((c) => ({ label: c.circle_name, value: c.circle_name })));
  }, [circlesList, selectedZoneName]);

  // ---- DISTRICT: everything, narrowed only by zone (if selected) ----
  const districts = useMemo(() => {
    const src = selectedZoneName
      ? districtsList.filter((d) => normalizeName(d.zone_name) === normalizeName(selectedZoneName))
      : districtsList;

    return dedupe(src.map((d) => ({ label: d.district_name, value: d.district_name })));
  }, [districtsList, selectedZoneName]);

  // ---- TALUKA: everything, narrowed by zone AND/OR district, whichever are set ----
  const talukas = useMemo(() => {
    let src = talukasList;
    if (selectedZoneName) {
      src = src.filter((t) => normalizeName(t.zone_name) === normalizeName(selectedZoneName));
    }
    if (filters.district) {
      src = src.filter((t) => normalizeName(t.district_name) === normalizeName(filters.district));
    }
    return dedupe(src.map((t) => ({ label: t.taluka_name, value: t.taluka_name })));
  }, [talukasList, selectedZoneName, filters.district]);

  // ---- Project-derived options: village / land type / status ----
  // narrowed by whatever of zone/circle/district/taluka is currently selected,
  // but never gated — always show the full list if nothing's selected yet
  const scopedProjects = useMemo(() => {
    return projects.filter((p) => {
      if (!projectMatchesZone(p, filters.zone, zonesList)) return false;
      if (!safeEquals(p.circle_name, filters.circle)) return false;
      if (!safeEquals(p.district_name, filters.district)) return false;
      if (!safeEquals(p.taluka_name, filters.taluka)) return false;
      return true;
    });
  }, [projects, filters.zone, filters.circle, filters.district, filters.taluka, zonesList]);

  const villages = useMemo(
    () => dedupe([...new Set(scopedProjects.map((p) => p.village).filter(Boolean))].map((v) => ({ label: v, value: v }))),
    [scopedProjects]
  );

  const landTypes = useMemo(
    () => dedupe([...new Set(scopedProjects.map((p) => p.land_type).filter(Boolean))].map((l) => ({ label: l, value: l }))),
    [scopedProjects]
  );

  const statuses = useMemo(
    () => dedupe([...new Set(scopedProjects.map((p) => p.status).filter(Boolean))].map((s) => ({ label: s, value: s }))),
    [scopedProjects]
  );

  const selectProps = {
    allowClear: true,
    className: "filter-chip",
    suffixIcon: <FilterOutlined style={{ color: "#da241c" }} />,
    style: { width: 175 },
    showSearch: true,
    filterOption: (input, option) =>
      (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
  };

  return (
    <div className="toolbar-wrapper">
      <div className="top-toolbar">
        <SearchBar projects={projects} onProjectSelect={onProjectSelect} />

        <div className="filters-slider">
          {showLeft && <button className="slider-arrow" onClick={slideLeft}><LeftOutlined /></button>}

          <div className="filters-track" ref={trackRef} onScroll={updateArrows}>
            {/* Zone */}
            <Select
              {...selectProps}
              placeholder="Zone"
              value={filters.zone}
              onChange={(value) => setFilters((prev) => ({ ...prev, zone: value }))}
              options={zonesList.map((z) => ({ label: z.name, value: z.code }))}
              loading={hierarchyLoading}
            />

            {/* Circle */}
            <Select
              {...selectProps}
              placeholder="Circle"
              value={filters.circle}
              onChange={(value) => setFilters((prev) => ({ ...prev, circle: value }))}
              options={circles}
            />

            {/* District */}
            <Select
              {...selectProps}
              placeholder="District"
              value={filters.district}
              onChange={(value) => setFilters((prev) => ({ ...prev, district: value }))}
              options={districts}
            />

            {/* Taluka */}
            <Select
              {...selectProps}
              placeholder="Taluka"
              value={filters.taluka}
              onChange={(value) => setFilters((prev) => ({ ...prev, taluka: value }))}
              options={talukas}
            />

            {/* Village */}
            <Select
              {...selectProps}
              placeholder="Village"
              value={filters.village}
              onChange={(v) => setFilters((p) => ({ ...p, village: v }))}
              options={villages}
            />

            {/* Land Type */}
            <Select
              {...selectProps}
              placeholder="Land Type"
              value={filters.landType}
              onChange={(v) => setFilters((p) => ({ ...p, landType: v }))}
              options={landTypes}
            />

            {/* Status */}
            <Select
              {...selectProps}
              placeholder="Status"
              value={filters.status}
              onChange={(v) => setFilters((p) => ({ ...p, status: v }))}
              options={statuses}
            />
          </div>

          {showRight && <button className="slider-arrow" onClick={slideRight}><RightOutlined /></button>}
        </div>
      </div>
    </div>
  );
}

function dedupe(options) {
  const seen = new Set();
  return options
    .filter((o) => {
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export default TopToolbar;